import { mount } from 'svelte'
import VideoPlayer from './components/VideoPlayer.svelte'

import {
  attributeStoreMapping,
  AttributeType,
  type AttributeName,
} from './mappings/AttributeStoreMapping'
import componentAttributesStore from './stores/componentAttributes.svelte'

class VideoPlayerComponent extends HTMLElement {
  private player = mount(VideoPlayer, {
    target: this.attachShadow({ mode: 'closed' }),
    props: {},
  })
  private eventQueue: Array<{
    type: string
    listener: EventListenerOrEventListenerObject
    options?: boolean | AddEventListenerOptions
  }> = []
  static get observedAttributes(): string[] {
    return Object.keys(attributeStoreMapping)
  }
  constructor() {
    super()

    this.initializeAttributes()
  }
  connectedCallback() {
    this.render()
  }
  render() {
    //this.syncStoreToAttributes()
    this.processEventQueue()
  }

  // when an attribute changes, update the componentAttributesStore accordingly
  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    if (oldValue !== newValue && attributeStoreMapping.hasOwnProperty(name)) {
      const { parse, property } = attributeStoreMapping[name as AttributeName]
      const parsedValue = parse(newValue)

      const state = componentAttributesStore.state as any
      state[property] =
        parsedValue === null
          ? componentAttributesStore.defaults[
              property as keyof typeof componentAttributesStore.defaults
            ]
          : parsedValue
    }
  }

  private initializeAttributes() {
    for (const [attrName, { parse, property, type }] of Object.entries(
      attributeStoreMapping,
    )) {
      const attrValue = this.getAttribute(attrName)
      const state = componentAttributesStore.state as any
      if (attrValue !== null) {
        const parsedValue = parse(attrValue)
        state[property] = parsedValue
      } else if (type === AttributeType.BOOLEAN) {
        state[property] = (componentAttributesStore.defaults as any)[property]
      }
    }
  }

  private processEventQueue(): void {
    while (this.eventQueue.length > 0) {
      const { type, listener, options } = this.eventQueue.shift()!
      this.player.addEventListener(type, listener, options)
    }
  }

  /*private syncStoreToAttributes() {
    $effect(() => {
      const snapshot = $state.snapshot(componentAttributesStore.state)

      for (const key of Object.keys(snapshot)) {
        const storeValue = snapshot[key as keyof typeof snapshot]
        const attrName = key as AttributeName

        const currentAttrValue = this.getAttribute(attrName)
        const stringValue =
          storeValue !== undefined && storeValue !== null
            ? String(storeValue)
            : null

        if (currentAttrValue !== stringValue) {
          if (stringValue === null) {
            this.removeAttribute(attrName)
          } else {
            this.setAttribute(attrName, stringValue)
          }
        }
      }
    })
  }*/

  // PLAY
  get isPlaying() {
    return this.player.getIsPlaying() ?? false
  }
  play() {
    this.player.play()
  }
  // PAUSE
  pause() {
    this.player.pause()
  }
  // VIDEO TYPE
  get videoType() {
    return this.player.getVideoType()
  }
  // PLAYBACK RATE
  get playbackRate() {
    return this.player.getPlaybackRate() ?? 1
  }
  set playbackRate(rate: number) {
    this.setAttribute('playback-rate', rate.toString())
  }
  // VOLUME
  get volume() {
    return this.player.getVolume() ?? 0
  }
  set volume(volume: number) {
    this.setVolume(volume)
  }
  setVolume(volume: number) {
    this.player.setVolume(volume < 0 ? 0 : volume > 1 ? 1 : volume)
  }
  // MUTED
  get muted() {
    return this.player.getMuted() ?? false
  }
  set muted(muted: boolean) {
    this.setAttribute('muted', (muted || 'false').toString())
    if (this.volume === 0) this.volume = 0.25
  }
  setMuted(muted: boolean) {
    this.player.setMuted(muted)
  }
  // DURATION
  get duration() {
    return this.player.getDuration() ?? 0
  }
  // AUTOPLAY
  set autoplay(autoplay: boolean) {
    this.setAttribute('autoplay', (autoplay || 'false').toString())
  }
  // LOOP
  set loop(loop: boolean) {
    this.setAttribute('loop', loop.toString())
  }
  get loop() {
    return this.player.getLoop() ?? false
  }
  // HIDE CONTROLS
  set hideControls(hideControls: boolean) {
    this.setAttribute('hide-controls', (hideControls || 'false').toString())
  }
  // SRC
  /*set src(url: string) {
    this.setAttribute('src', url)
  }*/
  // CURRENT TIME
  get currentTime() {
    return this.player.getCurrentTime() ?? 0
  }
  set currentTime(time: number) {
    this.player.setCurrentTime(time)
  }
  setCurrentTime(time: number) {
    this.player.setCurrentTime(time)
  }
  // SEEK
  seek(time: number) {
    this.player.seek(time)
  }
  // HIDE TITLE
  set hideTitle(hideTitle: boolean) {
    this.setAttribute('hide-title', (hideTitle || 'false').toString())
  }
  // HIDE POSTER
  set hidePoster(hidePoster: boolean) {
    this.setAttribute('hide-poster', (hidePoster || 'false').toString())
  }
  // SHOW CAPTIONS
  set showCaptions(showCaptions: boolean) {
    this.setAttribute('show-captions', (showCaptions || 'false').toString())
  }
  // PICTURE IN PICTURE
  exitPictureInPicture() {
    this.player.exitPictureInPicture()
  }
  requestPictureInPicture() {
    this.player.requestPictureInPicture()
  }
  getMp4Url() {
    return this.player.getMp4Url()
  }
  // FULLSCREEN
  set fullscreen(fullscreen: boolean) {
    if (fullscreen) {
      this.player.requestFullscreen()
    } else {
      this.player.exitFullscreen()
    }
  }
  // DOWNLOAD
  download(filename?: string) {
    this.player.download(filename)
  }
  // VIDEO SIZE
  getVideoSize() {
    return this.player.getVideoSize()
  }
  // EVENTS
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (!this.player) {
      this.eventQueue.push({ type, listener, options })
      return
    }
    this.player.addEventListener(type, listener, options)
  }
}

customElements.define('ovhcloud-player', VideoPlayerComponent)
